import React, { useState, useRef } from 'react';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import CategoryIcon, { getCategoryIconUrl } from './CategoryIcon';

const MainCategoryGrid = ({
  categories: dashboardCategories, // دیتا از داشبورد (اختیاری)
  title = 'دسته بندی محصولات اصلی',
  subtitle = 'انتخاب از بیش از 12 دسته اصلی',
}) => {
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const hoverTimeout = useRef(null);

  const navigate = useNavigate();

  // فقط از دیتای واقعی داشبورد/API استفاده می‌کنیم (دیگر mock نمایش داده نمی‌شود)
  const categories = Array.isArray(dashboardCategories) ? dashboardCategories : [];

  // فقط ۱۲ دسته‌ی اول برای نمایش در این سکشن
  const visibleCategories = categories;

  const getIcon = (iconName) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon className="w-10 h-10" /> : null;
  };

  // اگر هیچ دسته‌ای نداریم، یک پیام ملایم نشان بده تا کارفرما فکر نکند استاتیک است
  if (!visibleCategories || visibleCategories.length === 0) {
    return (
      <section className="relative z-30 py-16 bg-white" dir="rtl">
        <div className="pp-container">
          <div className="text-center text-sm text-gray-500">
            هنوز دسته‌بندی‌ای از داشبورد دریافت نشده است.
          </div>
        </div>
      </section>
    );
  }

  const handleMouseEnter = (id) => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setHoveredCategory(id);
  };

  const handleMouseLeave = () => {
    // کمی تأخیر تا هاور ناگهانی بسته نشود
    hoverTimeout.current = setTimeout(() => {
      setHoveredCategory(null);
    }, 150);
  };

  return (
    <section
      className="relative z-30 overflow-x-clip bg-white py-16"
    >
      <div className="pp-container">
        {/* عنوان و توضیح بالای گرید */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-gray-600">
            {subtitle || `انتخاب از ${visibleCategories.length} دسته اصلی`}
          </p>
        </div>

        {/* گرید ۲ ردیف ۶تایی در دسکتاپ */}
        <div className="flex flex-wrap justify-center gap-6">
          {visibleCategories.map((category) => {
            // اگر ساختار مثل مگامنو باشد:
            // category.groups = [{ id, title, ... }]
            // در غیر این صورت از category.subcategories استفاده می‌کنیم.
            // گروه‌های سطح ۲
            const subItems = Array.isArray(category.groups)
              ? category.groups
              : (category.children || []).map((c) => ({
                  id: c.slug || c.id,
                  title: c.title || c.name,
                  slug: c.slug,
                }));

            const displayName = category.name || category.title;
            const hasUploadedIcon = Boolean(getCategoryIconUrl(category));

            return (
              <div
                key={category.id}
                className="relative basis-[calc(50%-0.75rem)] sm:basis-[calc(33.333%-1rem)] lg:basis-[calc(16.666%-1.25rem)] min-w-[145px] max-w-[210px]"
                onMouseEnter={() => handleMouseEnter(category.id)}
                onMouseLeave={handleMouseLeave}
              >
                <div
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#005bbb] hover:shadow-xl transition-all cursor-pointer group"
                  onClick={() => {
                    if (category.slug) navigate(`/category/${category.slug}`);
                  }}
                >
                  <div className="text-center space-y-4">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full group-hover:bg-[#005bbb] group-hover:text-white transition-all">
                      <CategoryIcon
                        category={category}
                        fallback={getIcon(category.icon) || <Icons.Package className="w-10 h-10" />}
                        imageClassName={`${hasUploadedIcon ? 'h-14 w-14' : 'h-10 w-10'} object-contain`}
                      />
                    </div>
                    <h3 className="font-bold text-gray-900 group-hover:text-[#005bbb] transition-colors">
                      {displayName}
                    </h3>
                  </div>
                </div>

                {/* دراپ‌داون زیر دسته‌ها (سطح ۲) */}
                {hoveredCategory === category.id && subItems.length > 0 && (
                  <div
                    className="
                      absolute top-full right-0 max-w-[calc(100vw-2rem)]
                      bg-white border border-gray-200 rounded-xl shadow-2xl
                      z-50 p-4 min-w-[240px] md:min-w-[280px]
                    "
                    onMouseEnter={() => handleMouseEnter(category.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <h4 className="font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                      {displayName}
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {subItems.slice(0, 6).map((sub) => (
                        <button
                          type="button"
                          key={sub.id}
                          onClick={() => {
                            if (sub.slug) navigate(`/subcategory/${sub.slug}`);
                          }}
                          className="w-full text-right block text-sm text-gray-600 hover:text-[#005bbb] hover:bg-gray-50 p-2 rounded transition-colors"
                        >
                          {sub.title}
                        </button>
                      ))}
                      {subItems.length > 6 && (
                        <Button
                          type="button"
                          variant="link"
                          className="text-[#005bbb] text-sm w-full justify-start p-2"
                          onClick={() => {
                            if (category.slug) navigate(`/category/${category.slug}`);
                          }}
                        >
                          نمایش بیشتر
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default MainCategoryGrid;
